"use client";

type SaveProgressEntry = { stage: string; message: string };

type SaveProgressOverlayProps = {
    /** Active while request is in flight */
    isSaving: boolean;
    /** After save completes, keep log visible until dismissed or auto-close */
    showPostSaveReview: boolean;
    statusText: string | null;
    entries: SaveProgressEntry[];
    onDismissReview: () => void;
};

export function SaveProgressOverlay({
    isSaving,
    showPostSaveReview,
    statusText,
    entries,
    onDismissReview,
}: SaveProgressOverlayProps) {
    if (!isSaving && !showPostSaveReview) return null;

    const isReview = !isSaving && showPostSaveReview;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/30 backdrop-blur-[1px]">
            <div className="w-full max-w-xl rounded-xl border border-slate-200 bg-white px-6 py-5 shadow-xl">
                <p className="text-sm font-semibold text-slate-900">
                    {isReview ? "Save finished" : "Saving in progress"}
                </p>
                <p className="mt-1 text-sm text-slate-600">
                    {statusText ||
                        (isReview
                            ? "Review the steps below. You can close when you are done."
                            : "Please wait while we save, upsert, and sync deletions.")}
                </p>
                {!isReview ? (
                    <p className="mt-2 text-xs text-slate-500">
                        Buttons are temporarily disabled to prevent conflicting updates.
                    </p>
                ) : null}
                <div className="mt-4 max-h-52 space-y-2 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-3">
                    {entries.map((entry, idx) => (
                        <div key={`${entry.stage}-${entry.message}-${idx}`} className="flex items-start gap-2 text-xs">
                            <span
                                className={`mt-0.5 inline-block h-2 w-2 rounded-full ${
                                    entry.stage === "completed"
                                        ? "bg-emerald-500"
                                        : entry.stage === "failed"
                                          ? "bg-red-500"
                                          : "bg-indigo-500"
                                }`}
                            />
                            <span className="text-slate-700">{entry.message}</span>
                        </div>
                    ))}
                </div>
                {isReview ? (
                    <div className="mt-4 flex justify-end">
                        <button
                            type="button"
                            onClick={onDismissReview}
                            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                        >
                            Close
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
